import { call, put, takeEvery } from 'redux-saga/effects';
import callApi from 'utils/callApi';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';
import { fetchInventory, requestUpdateInventory } from 'redux/modules/inventory';

// Actions
export const CREATE_INVENTORY_STOCK_LINE = 'CREATE_INVENTORY_STOCK_LINE';
export const CREATE_INVENTORY_STOCK_LINE_SUCCESS = 'CREATE_INVENTORY_STOCK_LINE_SUCCESS';
export const CREATE_INVENTORY_STOCK_LINE_FAIL = 'CREATE_INVENTORY_STOCK_LINE_FAIL';
export const UPDATE_INVENTORY_STOCK_LINE = 'UPDATE_INVENTORY_STOCK_LINE';
export const UPDATE_INVENTORY_STOCK_LINE_SUCCESS = 'UPDATE_INVENTORY_STOCK_LINE_SUCCESS';
export const UPDATE_INVENTORY_STOCK_LINE_FAIL = 'UPDATE_INVENTORY_STOCK_LINE_FAIL';
export const DELETE_INVENTORY_STOCK_LINE = 'DELETE_INVENTORY_STOCK_LINE';
export const DELETE_INVENTORY_STOCK_LINE_SUCCESS = 'DELETE_INVENTORY_STOCK_LINE_SUCCESS';
export const DELETE_INVENTORY_STOCK_LINE_FAIL = 'DELETE_INVENTORY_STOCK_LINE_FAIL';

// Reducer
export default function inventoryStockLineReducer(state = {
  data: [], count: 0, loading: 0,
}, action) {
  const { type, payload } = action;
  switch (type) {
    case CREATE_INVENTORY_STOCK_LINE_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload['Product ID']) {
          return dataItem;
        }
        return {
          ...dataItem,
          'Stock Line': [
            payload['Stock Line'],
            ...dataItem['Stock Line'] || [],
          ],
          'Last Arrival Stock Line': payload['Stock Line'],
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case UPDATE_INVENTORY_STOCK_LINE_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload['Product ID']) {
          return dataItem;
        }
        const newStockLine = dataItem['Stock Line'] ? dataItem['Stock Line'].map((stockLineItem) => {
          if (stockLineItem.ID !== payload['Stock Line'].ID) {
            return stockLineItem;
          }
          return {
            ...stockLineItem,
            ...payload['Stock Line'],
          };
        }) : null;
        
        return {
          ...dataItem,
          ...newStockLine ? { 'Stock Line': newStockLine } : {},
          ...payload['Stock Line'].ID === dataItem['Last Arrival Stock Line'].ID ? {
            'Last Arrival Stock Line': {
              ...dataItem['Last Arrival Stock Line'],
              ...payload['Stock Line'],
            },
          } : {},
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    default:
      return state;
  }
}

// Action Creators
export function createInventoryStockLine(payload) {
  return { type: CREATE_INVENTORY_STOCK_LINE, payload };
}

export function createInventoryStockLineSuccess(payload) {
  return { type: CREATE_INVENTORY_STOCK_LINE_SUCCESS, payload };
}

export function createInventoryStockLineFail(payload) {
  return { type: CREATE_INVENTORY_STOCK_LINE_FAIL, payload };
}

export function updateInventoryStockLine(payload) {
  return { type: UPDATE_INVENTORY_STOCK_LINE, payload };
}

export function updateInventoryStockLineSuccess(payload) {
  return { type: UPDATE_INVENTORY_STOCK_LINE_SUCCESS, payload };
}

export function updateInventoryStockLineFail(payload) {
  return { type: UPDATE_INVENTORY_STOCK_LINE_FAIL, payload };
}

export function deleteInventoryStockLine(payload) {
  return { type: DELETE_INVENTORY_STOCK_LINE, payload };
}

export function deleteInventoryStockLineSuccess(payload) {
  return { type: DELETE_INVENTORY_STOCK_LINE_SUCCESS, payload };
}

export function deleteInventoryStockLineFail(payload) {
  return { type: DELETE_INVENTORY_STOCK_LINE_FAIL, payload };
}

// Requests
export function requestCreateInventoryStockLine(payload) {
  return callApi('inventory/stock_line/', 'POST', payload);
}

export function requestUpdateInventoryStockLine(payload) {
  return callApi('inventory/stock_line/', 'PATCH', payload);
}

export function requestDeleteInventoryStockLine(payload) {
  return callApi(`inventory/stock_line/?id=${payload.ID}`, 'DELETE');
}

// Sagas
function* createInventoryStockLineSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    if (values['Calories Per Unit']) {
      const productRes = yield call(requestUpdateInventory, {
        ID: values['Product ID'],
        'Calories Per Unit': values['Calories Per Unit'],
      });  
    }
    delete values['Calories Per Unit'];
    const res = yield call(requestCreateInventoryStockLine, values);
    yield put(createInventoryStockLineSuccess({
      'Product ID': values['Product ID'],
      'Stock Line': res.data,
    }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Products were successfully added to inventory',
      variant: 'success',
    }));
    if (resolve) {
      resolve({
        'Product ID': values['Product ID'],
        'Stock Line': res.data,
      });
    }
  } catch (err) {
    yield put(createInventoryStockLineFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateInventoryStockLineSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const productID = values['Product ID'];
    if (values['Calories Per Unit']) {
      const productRes = yield call(requestUpdateInventory, {
        ID: values['Product ID'],
        'Calories Per Unit': values['Calories Per Unit'],
      }); 
    }
    delete values['Calories Per Unit'];
    delete values['Product ID'];
    const res = yield call(requestUpdateInventoryStockLine, values);
    yield put(updateInventoryStockLineSuccess({
      'Product ID': productID,
      'Stock Line': res.data,
    }));
    yield put(fetchInventory({
      triggerLoading: false,
      ID: productID,
    }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    if (resolve) {
      resolve({
        'Product ID': productID,
        'Stock Line': res.data,
      });
    }
  } catch (err) {
    yield put(updateInventoryStockLineFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteInventoryStockLineSaga(action) {
  try {
    const res = yield call(requestDeleteInventoryStockLine, action.payload);
    if (res.data.success) {
      yield put(deleteInventoryStockLineSuccess(action.payload));
      yield put(fetchInventory({
        triggerLoading: false,
        ID: action.payload['Product ID'],
      }));
    } else {
      yield put(deleteInventoryStockLineFail(res.data));
    }
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(deleteInventoryStockLineFail(err.data));
  }
}

export function* inventoryStockLineSaga() {
  yield takeEvery(CREATE_INVENTORY_STOCK_LINE, createInventoryStockLineSaga);
  yield takeEvery(UPDATE_INVENTORY_STOCK_LINE, updateInventoryStockLineSaga);
  yield takeEvery(DELETE_INVENTORY_STOCK_LINE, deleteInventoryStockLineSaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/inventoryStockLine.js